import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, CalendarClock, Check, Copy, Fingerprint, KeyRound, Lock, LogOut, Plus, Search, ShieldCheck, ShieldOff, UserCheck, UserRoundX, Users, X } from "lucide-react";
import { ApiError, usePermission, type PlatformAccount, type TemporaryPassword } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Avatar, Badge, Button, DataTable, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Switch, copyText, notifySaved, saveFailureText } from "@ncp/ui";
import { SavedViewsMenu } from "../../components/SavedViews";
import { useAccountActions, useDirectory, usePlatformAccounts, useRoles, useUserActions, type DirectoryUser, type Role } from "./api";

interface DirectoryViewState { q: string; role: string; kind: "Staff" | "External"; includeInactive: boolean }

/** Users directory: staff and applicants, built-in + assigned roles, activate/deactivate, assign console roles with optional expiry. */
export function UsersDirectory() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const canWrite = can("platform.users.write");
  const canAccounts = can("platform.accounts.read");
  const canManageAccounts = can("platform.accounts.manage");
  const accounts = usePlatformAccounts(canAccounts);
  const [view, setView] = useState<DirectoryViewState>({ q: "", role: "", kind: "Staff", includeInactive: false });
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<DirectoryUser | null>(null);
  const users = useDirectory({ q: debounced, role: view.role || undefined, kind: view.kind, includeInactive: view.includeInactive });
  const roles = useRoles();
  const actions = useUserActions();
  const roleByKey = useMemo(() => new Map((roles.data ?? []).map((r) => [r.key, r])), [roles.data]);

  const columns = useMemo<ColumnDef<DirectoryUser>[]>(() => [
    {
      id: "user", header: t("admin:identity.users.columns.user"), accessorFn: (u) => u.displayName,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar name={row.original.displayName} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-medium">{locale === "ar" && row.original.displayNameAr ? row.original.displayNameAr : row.original.displayName}</p>
            <p className="truncate text-xs text-muted-foreground">{row.original.email ?? row.original.title ?? "—"}</p>
          </div>
        </div>
      ),
    },
    { id: "title", header: t("admin:identity.users.columns.title"), accessorFn: (u) => u.title ?? u.organizationName ?? "", cell: ({ row }) => <span className="text-sm">{row.original.kind === "External" ? <span className="inline-flex items-center gap-1"><Building2 className="size-3.5 text-sand-500" /> {row.original.organizationName ?? "—"}</span> : row.original.title ?? "—"}</span> },
    {
      id: "roles", header: t("admin:identity.users.columns.roles"), accessorFn: (u) => [...u.roles, ...u.dynamicRoles].join(" "),
      cell: ({ row }) => <RoleChips user={row.original} roleByKey={roleByKey} />,
    },
    { id: "lastLogin", header: t("admin:identity.users.columns.lastLogin"), accessorFn: (u) => u.lastLoginAt ?? "", cell: ({ row }) => <span className="text-xs text-muted-foreground">{row.original.lastLoginAt ? formatDateTime(row.original.lastLoginAt, locale) : t("admin:identity.users.never")}</span> },
    {
      id: "status", header: t("admin:identity.users.columns.status"), accessorFn: (u) => (u.isActive ? 1 : 0),
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <Badge variant={row.original.isActive ? "success" : "outline"} size="sm" dot>{row.original.isActive ? t("admin:identity.users.active") : t("admin:identity.users.inactive")}</Badge>
          {row.original.isDemoPersona ? <Badge variant="camel" size="sm">{t("admin:identity.users.demo")}</Badge> : null}
          {row.original.identityProviderKey ? <Badge variant="info" size="sm" title={t("admin:identity.users.viaProvider", { name: row.original.identityProviderKey })}><Fingerprint /> {row.original.identityProviderKey}</Badge> : null}
        </div>
      ),
    },
  ], [locale, roleByKey, t]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-sand-500" />
          <Input value={view.q} onChange={(e) => { setView({ ...view, q: e.target.value }); window.clearTimeout((window as unknown as { __dirT?: number }).__dirT); (window as unknown as { __dirT?: number }).__dirT = window.setTimeout(() => setDebounced(e.target.value), 250); }} placeholder={t("admin:identity.users.search")} className="ps-9" />
        </div>
        <NativeSelect value={view.kind} onChange={(e) => setView({ ...view, kind: e.target.value as "Staff" | "External", role: "" })} className="h-10 w-40">
          <option value="Staff">{t("admin:identity.users.kinds.Staff")}</option>
          <option value="External">{t("admin:identity.users.kinds.External")}</option>
        </NativeSelect>
        <NativeSelect value={view.role} onChange={(e) => setView({ ...view, role: e.target.value })} className="h-10 w-52">
          <option value="">{t("admin:identity.users.anyRole")}</option>
          {(view.kind === "Staff" ? roles.data ?? [] : []).map((r) => <option key={r.key} value={r.key}>{locale === "ar" && r.nameAr ? r.nameAr : r.name}</option>)}
        </NativeSelect>
        <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={view.includeInactive} onCheckedChange={(v) => setView({ ...view, includeInactive: v })} /> {t("admin:identity.users.includeInactive")}</label>
        <div className="ms-auto flex items-center gap-2">
          <Badge variant="outline" size="sm" className="tabular"><Users /> {users.data?.length ?? 0}</Badge>
          <SavedViewsMenu tableKey="identity.users" current={view} onApply={(s) => { setView(s); setDebounced(s.q); }} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={users.data ?? []}
        loading={users.isLoading}
        pageSize={15}
        onRowClick={(u) => setSelected(u)}
        rowClassName={(u) => (u.isActive ? undefined : "opacity-60")}
        emptyState={<EmptyState icon={Users} title={t("admin:identity.users.empty")} />}
      />

      <UserSheet user={selected} roles={roles.data ?? []} onClose={() => setSelected(null)} canWrite={canWrite} actions={actions}
        account={selected && accounts.data ? (accounts.data.find((a) => a.userId === selected.id) ?? null) : null} canManageAccount={canManageAccounts} />
    </div>
  );
}

function RoleChips({ user, roleByKey, max = 3 }: { user: DirectoryUser; roleByKey: Map<string, Role>; max?: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const all = [...user.roles.map((r) => ({ key: r, dynamic: false })), ...user.dynamicRoles.filter((r) => !user.roles.includes(r)).map((r) => ({ key: r, dynamic: true }))];
  const label = (k: string) => { const r = roleByKey.get(k); return r ? (locale === "ar" && r.nameAr ? r.nameAr : r.name) : t(`admin:identity.roles.builtInNames.${k}`, { defaultValue: k }); };
  return (
    <div className="flex flex-wrap gap-1">
      {all.slice(0, max).map((r) => {
        const role = roleByKey.get(r.key);
        return <Badge key={r.key} variant={r.dynamic ? "gold" : "outline"} size="sm"><span className="inline-block size-2 rounded-full" style={{ background: role?.color ?? "#b8b0a0" }} aria-hidden /> {label(r.key)}</Badge>;
      })}
      {all.length > max ? <Badge variant="outline" size="sm">+{all.length - max}</Badge> : null}
      {all.length === 0 ? <span className="text-xs text-muted-foreground">—</span> : null}
    </div>
  );
}

function UserSheet({ user, roles, onClose, canWrite, actions, account, canManageAccount }: { user: DirectoryUser | null; roles: Role[]; onClose: () => void; canWrite: boolean; actions: ReturnType<typeof useUserActions>; account: PlatformAccount | null; canManageAccount: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [assigning, setAssigning] = useState(false);
  const [roleKey, setRoleKey] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const assignable = roles.filter((r) => r.assignable && r.enabled && !(user?.roles.includes(r.key) || user?.dynamicRoles.includes(r.key)));
  const roleName = (k: string) => { const r = roles.find((x) => x.key === k); return r ? (locale === "ar" && r.nameAr ? r.nameAr : r.name) : t(`admin:identity.roles.builtInNames.${k}`, { defaultValue: t(`roles.${k}`, { defaultValue: k }) }); };

  return (
    <Sheet open={!!user} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="end" width="34rem">
        {user ? (
          <>
            <SheetHeader>
              <div className="flex items-center gap-3">
                <Avatar name={user.displayName} size="lg" />
                <div className="min-w-0">
                  <SheetTitle className="truncate">{locale === "ar" && user.displayNameAr ? user.displayNameAr : user.displayName}</SheetTitle>
                  <SheetDescription className="truncate">{user.email ?? "—"}{user.title ? ` · ${user.title}` : ""}{user.organizationName ? ` · ${user.organizationName}` : ""}</SheetDescription>
                </div>
              </div>
            </SheetHeader>
            <SheetBody className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={user.isActive ? "success" : "outline"} dot>{user.isActive ? t("admin:identity.users.active") : t("admin:identity.users.inactive")}</Badge>
                <Badge variant="outline">{t(`admin:identity.users.kinds.${user.kind}`)}</Badge>
                {user.isDemoPersona ? <Badge variant="camel">{t("admin:identity.users.demo")}</Badge> : null}
                {user.identityProviderKey ? <Badge variant="info"><Fingerprint /> {t("admin:identity.users.viaProvider", { name: user.identityProviderKey })}</Badge> : null}
                <span className="ms-auto text-xs text-muted-foreground"><CalendarClock className="me-1 inline size-3.5" />{user.lastLoginAt ? t("admin:identity.users.lastLogin", { when: formatDateTime(user.lastLoginAt, locale) }) : t("admin:identity.users.never")}</span>
              </div>

              {account ? <AccountSection account={account} canManage={canManageAccount} /> : null}

              <section>
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="size-4 text-aegold-700" /> {t("admin:identity.users.builtInRoles")}</h4>
                {user.roles.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
                  <div className="flex flex-wrap gap-1.5">
                    {user.roles.map((r) => (
                      <Badge key={r} variant="outline" title={user.directoryRoles?.includes(r) ? t("admin:identity.users.fromDirectory") : undefined}>
                        {roleName(r)}{user.directoryRoles?.includes(r) ? <Fingerprint className="text-info-600" aria-label={t("admin:identity.users.fromDirectory")} /> : null}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-muted-foreground">{t("admin:identity.users.builtInRolesHint")}</p>
              </section>

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="flex items-center gap-2 text-sm font-semibold"><Users className="size-4 text-aegold-700" /> {t("admin:identity.users.assignedRoles")}</h4>
                  {canWrite && user.kind === "Staff" ? <Button size="sm" variant="outline" onClick={() => { setAssigning(true); setRoleKey(assignable[0]?.key ?? ""); setValidUntil(""); }} disabled={assignable.length === 0}><Plus /> {t("admin:identity.users.assign")}</Button> : null}
                </div>
                {user.dynamicRoles.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:identity.users.noAssigned")}</p> : (
                  <ul className="divide-y divide-sand-100 rounded-lg border border-sand-200">
                    {user.dynamicRoles.map((r) => {
                      const role = roles.find((x) => x.key === r);
                      return (
                        <li key={r} className="flex items-center gap-3 px-3 py-2 text-sm">
                          <span className="inline-block size-2.5 rounded-full" style={{ background: role?.color ?? "#b8b0a0" }} aria-hidden />
                          <span className="flex-1"><span className="font-medium">{roleName(r)}</span><span className="ms-2 font-mono text-[11px] text-muted-foreground">{r}</span></span>
                          {user.directoryRoles?.includes(r) ? <Badge variant="info" size="sm"><Fingerprint /> {t("admin:identity.users.fromDirectory")}</Badge> : null}
                          {canWrite ? <Button size="sm" variant="ghost" aria-label={t("admin:identity.users.unassign")} loading={actions.unassign.isPending} onClick={() => actions.unassign.mutate({ userId: user.id, roleKey: r }, { onSuccess: (answer) => { notifySaved(answer, t("admin:identity.users.toasts.unassigned")); onClose(); } })}><X /></Button> : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {assigning ? (
                  <div className="mt-3 space-y-3 rounded-lg border border-aegold-200 bg-aegold-50/40 p-3">
                    <Field label={t("admin:identity.users.role")}>
                      <NativeSelect value={roleKey} onChange={(e) => setRoleKey(e.target.value)}>
                        {assignable.map((r) => <option key={r.key} value={r.key}>{locale === "ar" && r.nameAr ? r.nameAr : r.name}</option>)}
                      </NativeSelect>
                    </Field>
                    <Field label={t("admin:identity.users.validUntil")} hint={t("admin:identity.users.validUntilHint")}><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setAssigning(false)}>{t("admin:common.cancel")}</Button>
                      <Button size="sm" variant="gold" loading={actions.assign.isPending} disabled={!roleKey} onClick={() => actions.assign.mutate({ userId: user.id, roleKey, validUntil: validUntil ? new Date(validUntil + "T23:59:59").toISOString() : null }, { onSuccess: (answer) => { notifySaved(answer, t("admin:identity.users.toasts.assigned")); setAssigning(false); onClose(); } })}>{t("admin:identity.users.assign")}</Button>
                    </div>
                  </div>
                ) : null}
              </section>

              {canWrite ? (
                <section className="rounded-lg border border-sand-200 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{user.isActive ? t("admin:identity.users.deactivate") : t("admin:identity.users.activate")}</p>
                      <p className="text-xs text-muted-foreground">{user.isActive ? t("admin:identity.users.deactivateHint") : t("admin:identity.users.activateHint")}</p>
                    </div>
                    {user.isActive ? <Button size="sm" variant="outline" onClick={() => setConfirmDeactivate(true)}><UserRoundX /> {t("admin:identity.users.deactivate")}</Button>
                      : <Button size="sm" variant="success" loading={actions.setActive.isPending} onClick={() => actions.setActive.mutate({ userId: user.id, active: true }, { onSuccess: (answer) => { notifySaved(answer, t("admin:identity.users.toasts.activated")); onClose(); } })}><UserCheck /> {t("admin:identity.users.activate")}</Button>}
                  </div>
                </section>
              ) : null}
            </SheetBody>
            <Dialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
              <DialogContent size="sm">
                <DialogHeader><DialogTitle>{t("admin:identity.users.deactivate")}</DialogTitle><DialogDescription>{t("admin:identity.users.deactivateConfirm", { name: user.displayName })}</DialogDescription></DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmDeactivate(false)}>{t("admin:common.cancel")}</Button>
                  <Button variant="destructive" loading={actions.setActive.isPending} onClick={() => actions.setActive.mutate({ userId: user.id, active: false }, { onSuccess: (answer) => { notifySaved(answer, t("admin:identity.users.toasts.deactivated")); setConfirmDeactivate(false); onClose(); } })}>{t("admin:identity.users.deactivate")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
/**
 * The person's password + authenticator account (security §2.8, §14.4): its state, the second factor and sessions, and the actions of
 * the account administrator. A one-time password is shown once, from the response that issued it, and handed over in person.
 */
function AccountSection({ account, canManage }: { account: PlatformAccount; canManage: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const actions = useAccountActions();
  const [temp, setTemp] = useState<TemporaryPassword | null>(null);
  const [copied, setCopied] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const state = account.isActive ? account.status : "deactivated";
  const fail = (e: unknown) => {
    const code = e instanceof ApiError ? (e.problem?.code ?? null) : null;
    setRefusal(code === "second_admin_required" ? t("admin:identity.users.account.secondAdmin") : code === "self_change_not_allowed" ? t("admin:identity.users.account.selfChange") : saveFailureText(e) || t("admin:common.error"));
  };
  // The account actions report no toast of their own (report: "silent"): a refusal is shown here, beside the account.
  const run = (m: { mutateAsync: (id: string) => Promise<unknown> }, keep?: (r: unknown) => void) => {
    setRefusal(null);
    void m.mutateAsync(account.userId).then((r) => { keep?.(r); notifySaved(r, t("admin:identity.users.account.done")); }).catch(fail);
  };
  return (
    <section className="rounded-lg border border-sand-200 p-3" data-testid="account-section">
      <h4 className="flex items-center gap-2 text-sm font-semibold"><KeyRound className="size-4 text-aegold-700" /> {t("admin:identity.users.account.title")}</h4>
      <p className="mt-1 text-xs text-muted-foreground">{t("admin:identity.users.account.hint")}</p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
        <Badge variant={state === "active" ? "success" : state === "locked" ? "danger" : state === "deactivated" ? "outline" : "warning"} size="sm" dot>{t(`admin:identity.users.account.status.${state}`)}</Badge>
        <Badge variant="outline" size="sm">{t("admin:identity.users.account.factor")}: {account.mfaEnrolled ? t("admin:identity.users.account.factorOn") : t("admin:identity.users.account.factorOff")}</Badge>
        <Badge variant="outline" size="sm">{t("admin:identity.users.account.sessions", { count: account.activeSessions })}</Badge>
      </div>
      {temp ? (
        <div className="mt-3 rounded-lg border border-aegold-300 bg-aegold-50 p-3" data-testid="temporary-password">
          <p className="text-sm font-semibold">{t("admin:identity.users.account.tempTitle")}</p>
          <div className="mt-2 flex items-center gap-2">
            <code dir="ltr" className="min-w-0 flex-1 select-all break-all rounded-md border border-sand-200 bg-white px-2 py-1.5 font-mono text-sm font-semibold tracking-wider">{temp.temporaryPassword}</code>
            <Button size="sm" variant="outline" onClick={() => void copyText(temp.temporaryPassword, t("admin:identity.users.account.copied")).then((ok) => { if (ok) setCopied(true); })}>{copied ? <Check /> : <Copy />} {copied ? t("admin:identity.users.account.copied") : t("admin:identity.users.account.copy")}</Button>
          </div>
          <p className="mt-2 text-xs font-medium text-aeblack-900">{t("admin:identity.users.account.tempHint")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("admin:identity.users.account.tempExpires", { when: formatDateTime(temp.expiresAt, locale) })}</p>
        </div>
      ) : null}
      {refusal ? <p className="mt-2 text-xs font-medium text-danger-700" role="alert">{refusal}</p> : null}
      {canManage && state !== "directory" ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {state === "no_credential" ? (
            <Button size="sm" variant="outline" loading={actions.issue.isPending} onClick={() => run(actions.issue, (r) => setTemp(r as TemporaryPassword))}><KeyRound /> {t("admin:identity.users.account.issue")}</Button>
          ) : (
            <>
              <Button size="sm" variant="outline" loading={actions.resetPassword.isPending} onClick={() => run(actions.resetPassword, (r) => setTemp(r as TemporaryPassword))}><KeyRound /> {t("admin:identity.users.account.resetPassword")}</Button>
              <Button size="sm" variant="outline" loading={actions.resetMfa.isPending} onClick={() => run(actions.resetMfa)}><ShieldOff /> {t("admin:identity.users.account.resetMfa")}</Button>
            </>
          )}
          {state === "locked" ? <Button size="sm" variant="outline" loading={actions.unlock.isPending} onClick={() => run(actions.unlock)}><Lock /> {t("admin:identity.users.account.unlock")}</Button> : null}
          {account.activeSessions > 0 ? <Button size="sm" variant="outline" loading={actions.signOut.isPending} onClick={() => run(actions.signOut)}><LogOut /> {t("admin:identity.users.account.signOut")}</Button> : null}
        </div>
      ) : null}
    </section>
  );
}
